import {
  BottomTextContainer,
  LeftContainer,
  NotificationContainer,
  NotificationWrapper,
  PepPriceContainer,
  PepPriceHeading,
  PricesContainer,
  RightContainer,
  ToggleContainer,
  ErrorCardContainer,
  ErrorCardButton,
  TooltipContentWrapper,
} from './Styles'
import { useCallback, useContext, useEffect, useRef, type FocusEvent as ReactFocusEvent } from 'react'
import { LegalTextRenderer } from '../../../../utils/text/TextRenderer'
import { SiteBuilderContext } from 'src/siteBuilder/renderer/context/SiteBuilderContext'
import StatusError from '@telus-uds/palette-allium/build/web/icons/StatusError'
import {
  Typography,
  PriceLockup,
  Divider,
  Tooltip,
  ToggleSwitch,
  Skeleton,
  StackView,
  Box,
  Button,
  Icon,
} from '@telus-uds/components-web'
import {
  usePepPricingContext,
  setBIBToggleAnalyticEvent,
  formatPrice,
  getLocaleTag,
  PepPricingState,
} from './usePepPricing'

export interface PepPriceProps {
  defaultProductId?: string
  entryTitle?: string
  headingPriceSection?: string
  notificationTextContent?: string
  tooltipTextContent?: string
  legalCalloutBanner?: string
  retailPriceLabel?: string
  upfrontPriceText?: string
  monthlyPriceText?: string
  upfrontLegalText?: string
  monthlyLegalText?: string
  upfrontBottomText?: string
  monthlyBottomText?: string
  ctaText?: string
  ctaLink?: string
  preOrderCTAText?: string
  preOrderCTALink?: string
  errorCardMessage?: string
  errorButtonText?: string
  incaTrackingCode?: string
  priceTextSize?: string
  priceSignDirection?: string
  stickyPriceBarEnabled?: boolean
  priceSectionEnabled?: boolean
  disabledPriceText?: string
}

// Only one footnote-focus-out listener should ever be pending at a time, since the drawer is a single
// element shared across the whole page.
let pendingFocusOutCleanup: (() => void) | null = null

const PepPrice = ({
  headingPriceSection,
  notificationTextContent,
  tooltipTextContent,
  legalCalloutBanner,
  retailPriceLabel,
  priceTextSize,
  priceSignDirection,
  upfrontPriceText,
  monthlyPriceText,
  upfrontBottomText,
  monthlyBottomText,
  monthlyLegalText,
  upfrontLegalText,
  ctaText,
  ctaLink,
  preOrderCTAText,
  preOrderCTALink,
  stickyPriceBarEnabled,
  priceSectionEnabled,
  disabledPriceText,
  errorCardMessage,
  errorButtonText,
}: PepPriceProps) => {
  const { pricing, isLoading, error, bibEnabled, setBibEnabled, displayMonthly, displayUpfront, setStickyBarText } =
    usePepPricingContext() as PepPricingState

  const { locale } = useContext(SiteBuilderContext)
  const localeTag = getLocaleTag(locale?.language)

  const hasTrackedToggle = useRef(false)

  const handleToggleChange = (newValue: boolean) => {
    setBibEnabled(newValue)
    if (!hasTrackedToggle.current) {
      hasTrackedToggle.current = true
      setBIBToggleAnalyticEvent({ isToggleEnabled: newValue, URL: window.location.href })
    }
  }

  // Tracks this instance's own tooltip content node (not any other tooltip that may be open
  // elsewhere on the page) so focus-restoration below only ever reclaims focus that returned
  // into this tooltip, never one belonging to a different PepPrice instance.
  const tooltipContentRef = useRef<HTMLDivElement | null>(null)

  // Keeps the tooltip open when its content (or the footnote link inside it) takes focus.
  const attachTooltipContentNode = useCallback((node: HTMLDivElement | null) => {
    tooltipContentRef.current = node
    if (!node) return
    node.addEventListener('mousedown', (event) => event.preventDefault())
  }, [])

  const handleNotificationBlurCapture = (event: ReactFocusEvent<HTMLDivElement>) => {
    // The tooltip's content (including the footnote link) renders in a portal appended to
    // document.body, so it's never a DOM descendant of this container - checking
    // tooltipContentRef directly is what actually catches focus moving into it.
    const movingWithinNotification =
      event.relatedTarget instanceof Node &&
      (event.currentTarget.contains(event.relatedTarget) || !!tooltipContentRef.current?.contains(event.relatedTarget))

    if (movingWithinNotification) {
      event.stopPropagation()
      return
    }

    const footnoteDrawer = document.querySelector('[data-testid="uds-footnote"]')
    if (!footnoteDrawer || !(event.relatedTarget instanceof Node) || !footnoteDrawer.contains(event.relatedTarget)) {
      return
    }

    // Also prevents UDS Tooltip's own onBlur-close (set on its trigger) from firing for this blur.
    event.stopPropagation()
    pendingFocusOutCleanup?.()

    const trigger = event.target as HTMLElement
    const tooltipContentNode = tooltipContentRef.current

    const handleFootnoteFocusOut = (focusOutEvent: FocusEvent) => {
      if (focusOutEvent.relatedTarget instanceof Node && footnoteDrawer.contains(focusOutEvent.relatedTarget)) {
        return
      }
      pendingFocusOutCleanup = null
      footnoteDrawer.removeEventListener('focusout', handleFootnoteFocusOut)

      // The drawer's "X" button returns focus to the footnote link, which never blurs the
      // trigger since it's still inside this tooltip's own content - without this, the trigger's
      // onBlur-close could never fire again. Scoped to this instance's content node so an
      // unrelated tooltip open elsewhere on the page can't steal focus back to this trigger.
      const returnedIntoThisTooltipContent =
        focusOutEvent.relatedTarget instanceof Node && !!tooltipContentNode?.contains(focusOutEvent.relatedTarget)

      if (
        !(focusOutEvent.relatedTarget instanceof HTMLElement) ||
        focusOutEvent.relatedTarget === document.body ||
        returnedIntoThisTooltipContent
      ) {
        trigger.focus()
      }
    }
    pendingFocusOutCleanup = () => footnoteDrawer.removeEventListener('focusout', handleFootnoteFocusOut)
    footnoteDrawer.addEventListener('focusout', handleFootnoteFocusOut)
  }

  useEffect(() => {
    setStickyBarText({
      ctaText,
      ctaLink,
      preOrderCTAText,
      preOrderCTALink,
      legalCalloutBanner,
      monthlyPriceText,
      upfrontPriceText,
      monthlyBottomText,
      upfrontBottomText,
      monthlyLegalText,
      upfrontLegalText,
      priceTextSize,
      priceSignDirection,
      stickyPriceBarEnabled,
      priceSectionEnabled,
      disabledPriceText,
      errorCardMessage,
      errorButtonText,
    })
  }, [
    ctaText,
    ctaLink,
    preOrderCTAText,
    preOrderCTALink,
    legalCalloutBanner,
    monthlyPriceText,
    upfrontPriceText,
    monthlyBottomText,
    upfrontBottomText,
    monthlyLegalText,
    upfrontLegalText,
    priceTextSize,
    priceSignDirection,
    stickyPriceBarEnabled,
    priceSectionEnabled,
    disabledPriceText,
    errorCardMessage,
    errorButtonText,
    setStickyBarText,
  ])

  if (priceSectionEnabled === false) return null

  return (
    <PepPriceContainer data-testid="pep-price-section">
      {isLoading ? (
        <StackView space={2} direction="column">
          <Skeleton characters={6} lines={1} />
          <StackView space={3} direction="row">
            <Skeleton characters={3} sizeIndex={6} />
            <StackView space={2}>
              <Skeleton characters={3} sizeIndex={6} />
            </StackView>
          </StackView>
          <Skeleton characters={7} sizeIndex={8} />
          <Skeleton characters={6} lines={1} />
        </StackView>
      ) : error ? (
        <Box variant={{ background: 'danger' }} space={4}>
          <ErrorCardContainer>
            <Icon icon={StatusError} variant={{ color: 'danger' }} />
            <Typography>{errorCardMessage}</Typography>
          </ErrorCardContainer>
          <ErrorCardButton>
            <Button variant={{ danger: true }} onPress={() => window.location.reload()}>
              {errorButtonText}
            </Button>
          </ErrorCardButton>
        </Box>
      ) : (
        <>
          <PepPriceHeading>
            <Typography variant={{ size: 'h3' }} heading="h2">
              {headingPriceSection}
            </Typography>
          </PepPriceHeading>
          <PricesContainer>
            <LeftContainer>
              <PriceLockup
                price={formatPrice(displayMonthly, localeTag)}
                rateText={monthlyPriceText}
                bottomText={monthlyBottomText}
                size={priceTextSize}
                signDirection={priceSignDirection}
                copy={locale?.language ?? 'en'}
                tokens={{
                  priceMarginBottom: 'size0',
                  footnoteMarginTop: 'size0',
                  dividerColor: 'transparent',
                  bottomTextFontSize: '12px',
                }}
              />
              <LegalTextRenderer>{monthlyLegalText}</LegalTextRenderer>
            </LeftContainer>
            <Divider vertical variant={{ decorative: true }} />
            <RightContainer>
              <PriceLockup
                price={formatPrice(displayUpfront, localeTag)}
                rateText={upfrontPriceText}
                bottomText={upfrontBottomText}
                size={priceTextSize}
                signDirection={priceSignDirection}
                copy={locale?.language ?? 'en'}
                tokens={{
                  priceMarginBottom: 'size0',
                  footnoteMarginTop: 'size0',
                  dividerColor: 'transparent',
                  bottomTextFontSize: '12px',
                }}
              />
              <LegalTextRenderer>{upfrontLegalText}</LegalTextRenderer>
            </RightContainer>
          </PricesContainer>
          {pricing?.isBibEligible && (
            <NotificationWrapper>
              <NotificationContainer onBlurCapture={handleNotificationBlurCapture}>
                <Typography variant={{ size: 'size16' }}>
                  <LegalTextRenderer>{notificationTextContent}</LegalTextRenderer>
                </Typography>
                {tooltipTextContent?.trim() && (
                  <Tooltip
                    content={
                      <TooltipContentWrapper ref={attachTooltipContentNode}>
                        <LegalTextRenderer>{tooltipTextContent}</LegalTextRenderer>
                      </TooltipContentWrapper>
                    }
                  />
                )}
              </NotificationContainer>
              <ToggleContainer>
                <ToggleSwitch initialValue={bibEnabled} onChange={handleToggleChange} />
              </ToggleContainer>
            </NotificationWrapper>
          )}
          <BottomTextContainer>
            <Typography tokens={{ fontWeight: 300 }} variant={{ size: 'micro', colour: 'light' }}>
              <LegalTextRenderer>{legalCalloutBanner}</LegalTextRenderer>
            </Typography>
            {pricing?.retailBasePrice !== undefined && (
              <Typography tokens={{ fontWeight: 300 }} variant={{ size: 'micro', colour: 'light' }}>
                {retailPriceLabel}{' '}
                {priceSignDirection === 'right' ? (
                  <>{formatPrice(pricing.retailBasePrice, localeTag)}&nbsp;$</>
                ) : (
                  `$${formatPrice(pricing.retailBasePrice, localeTag)}`
                )}
              </Typography>
            )}
          </BottomTextContainer>
        </>
      )}
    </PepPriceContainer>
  )
}

export default PepPrice
